import { useGetEntity } from "@/queries/digital-files/entities.api";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Heading,
  Icon,
  TextField,
  useToast,
} from "@elaniintc/goes-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import FieldCard from "../entity/components/FieldCard";
import { useHeaderButton } from "@/contexts/header-button-context";
import { getFieldTypeInfo, getSectionsFromEntity } from "../utils";
import type {
  AssociatedRegistryType,
  EntityAssociateType,
  FieldType,
} from "@/schemas/digital-files";
import { UsersList } from "./components/UsersList";
import { IdField } from "./components/IdField";
import {
  useGetAutoID,
  useGetRegistry,
  useSaveNewRegistry,
  useUpdateRegistry,
} from "@/queries/digital-files/registries.api";
import { RegistryFormField } from "./components/RegistryFormField";
import {
  RegistryFormProvider,
  useRegistryForm,
} from "./contexts/RegistryFormContext";
import { uploadFile, useDeleteFile } from "@/queries/files/file.api";
import { RegistryList } from "./components/RegistryList";
import {
  getFieldsToSave,
  getFilesToSave,
  getFilesToDelete,
  compareIdArrays,
  findInitialDataCenterOptionByName,
  findInitialFieldValueByName,
  getFieldDefaultValue,
  normalizeValueForComparison,
} from "./utils";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmationModalBase } from "@/components/common/modals";

interface SectionElement {
  id: string;
  title: string;
  order: number;
  fields: FieldType[];
}

interface EntityRegistryPageContentProps {
  onCancel: () => void;
}

export const EntityRegistryPage = () => {
  const { id, registryId } = useParams();
  const navigate = useNavigate();
  const [openRegistryChangesModal, setOpenRegistryChangesModal] =
    useState(false);

  const handleCancel = () => {
    setOpenRegistryChangesModal(true);
  };

  const confirmExit = () => {
    setOpenRegistryChangesModal(false);
    navigate(
      `/expedientes${id && registryId ? `/entidad/${id}/detalle-registro/${registryId}` : id ? `?entity=${id}` : ""}`,
    );
  };

  return (
    <>
      <ConfirmationModalBase
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        isOpen={openRegistryChangesModal}
        onAccept={confirmExit}
        onClose={() => setOpenRegistryChangesModal(false)}
        acceptVariant="danger-secondary"
      />
      <RegistryFormProvider>
        <EntityRegistryPageContent onCancel={handleCancel} />
      </RegistryFormProvider>
    </>
  );
};

const EntityRegistryPageContent = ({
  onCancel,
}: EntityRegistryPageContentProps) => {
  const { id: entityId, registryId } = useParams();
  const { data: entityData, isLoading } = useGetEntity(entityId!, {
    includeFields: true,
    includeRoles: true,
  });
  const { data: registryData, isLoading: isLoadingRegistry } =
    useGetRegistry(registryId);
  const [sections, setSections] = useState<SectionElement[]>([]);
  const { setComp } = useHeaderButton();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: saveRegistry, isPending } = useSaveNewRegistry(
    entityId!,
  );
  const { mutateAsync: updateRegistryFields, isPending: isUpdating } =
    useUpdateRegistry(entityId!);
  const { mutateAsync: deleteFileMutation } = useDeleteFile();
  const { showToast } = useToast();
  const {
    isFormValid,
    fieldValues,
    setFieldValues,
    fieldOptions,
    setFieldOptions,
    users,
    regName,
    setRegName,
    regId,
    setRegId,
    setUsers,
    registries,
    setRegistries,
    setRolesList,
  } = useRegistryForm();
  const { data: autoId } = useGetAutoID(!registryId);
  const autoIdCode = autoId?.data.code;
  const registryName = registryData?.name || entityData?.name;

  // Función para transformar fieldOptions al formato de API
  const transformFieldOptionsForAPI = (
    options: Record<
      string,
      string | { id: string; table: string; display: string; source: string }
    >,
  ) => {
    const transformed: Record<string, unknown> = {};

    for (const [fieldId, option] of Object.entries(options)) {
      if (typeof option === "string") {
        // Opción local, mantener como string
        transformed[fieldId] = option;
      } else {
        // Opción de data center, ya está en el formato correcto
        transformed[fieldId] = option;
      }
    }

    return transformed;
  };

  const handleSubmit = useCallback(async () => {
    try {
      const transformedOptions = transformFieldOptionsForAPI(fieldOptions);
      const fieldsWithoutFiles = getFieldsToSave(
        sections,
        fieldValues,
        registryData?.field_values || [],
      );
      if (!registryId) {
        const newRegistry = await saveRegistry({
          name: regName,
          uniqueCode: regId,
          registries: fieldsWithoutFiles,
          associates: users,
          selectedOptions: transformedOptions,
          relatedRegistries: registries.map((r) => r.id),
        });
        if (
          newRegistry?.data?.field_values &&
          newRegistry?.data?.field_values.length > 0
        ) {
          const fileFields = getFilesToSave(
            sections,
            fieldValues,
            newRegistry.data.field_values ?? [],
          );
          await handleUploadFiles(fileFields);
        }
      } else {
        // Remove files that were deleted from the form
        const filesToDelete = getFilesToDelete(
          sections,
          fieldValues,
          registryData,
        );
        // Update registry fields
        await handleDeleteFiles(filesToDelete);
        const updated = await updateRegistryFields({
          registryId,
          name: regName,
          uniqueCode: registryData?.unique_code != regId ? regId : undefined,
          registries: fieldsWithoutFiles,
          associates: users,
          selectedOptions: transformedOptions,
          relatedRegistries: registries.map((r) => r.id),
        });
        // Upload new files
        const fileFields = getFilesToSave(
          sections,
          fieldValues,
          updated.data.field_values ?? [],
        );
        await handleUploadFiles(fileFields);
      }

      // Invalidar queries
      await queryClient.invalidateQueries({
        queryKey: ["registry-detail"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["generate-auto-id"],
      });

      showToast({
        title: "Registro guardado",
        description: "El registro se ha guardado correctamente.",
        options: {
          type: "success",
        },
      });
      goBack();
    } catch (error) {
      console.error("Error saving registry:", error);
      showToast({
        title: "Registro no guardado",
        description: "El registro no se ha guardado correctamente.",
        options: {
          type: "error",
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues, fieldOptions, registries, regName, regId, sections, users]);

  // Detect if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    const isCreate = !registryId;

    if (isLoading || isLoadingRegistry) return false;
    if (!entityData) return false;

    // Guard: If fieldValues is empty, we consider it not yet initialized.
    // This prevents false positives before the initialization useEffect runs.
    if (Object.keys(fieldValues).length === 0) return false;

    if (!isCreate && !registryData) return false;

    if (!isCreate && registryData && regName === "" && registryData.name !== "")
      return false;

    // ===== CREATE MODE =====
    if (isCreate) {
      // Detect any changes from the initial empty state
      if (regName.trim() !== "") return true;
      if (users.length > 0) return true;
      if (registries.length > 0) return true;

      // Check each field against its default value
      for (const field of entityData.fields) {
        const currentVal = fieldValues[field.id];
        const defaultVal = getFieldDefaultValue(field);

        if (
          normalizeValueForComparison(currentVal) !==
          normalizeValueForComparison(defaultVal)
        ) {
          return true;
        }

        // Check Data Center options
        const currentOpt = fieldOptions[field.id];
        if (currentOpt && normalizeValueForComparison(currentOpt) !== "") {
          return true;
        }
      }

      return false;
    }

    // ===== EDIT MODE =====

    const initialName = registryData?.name || "";
    if (
      normalizeValueForComparison(regName) !==
      normalizeValueForComparison(initialName)
    ) {
      return true;
    }

    // 2. Compare associated users
    const initialUsers = registryData?.associates || [];
    const initialUsersIds = initialUsers.map(
      (a: EntityAssociateType) => a.applicant_id,
    );
    const currentUsersIds = users.map((u) => u.applicant_id);

    if (!compareIdArrays(initialUsersIds, currentUsersIds)) {
      return true;
    }

    // 3. Compare related registries
    const initialRegistries = registryData?.related_registries || [];
    const initialRegIds = initialRegistries.map(
      (r: AssociatedRegistryType) => r.id,
    );
    const currentRegIds = registries.map((r) => r.id);

    if (!compareIdArrays(initialRegIds, currentRegIds)) {
      return true;
    }

    // 4. Compare each form field
    for (const field of entityData.fields) {
      const fieldName = field.name;

      // Compare field value
      const currentVal = fieldValues[field.id];
      const initialVal = findInitialFieldValueByName(
        fieldName,
        field.field_type,
        registryData,
      );

      if (
        normalizeValueForComparison(currentVal) !==
        normalizeValueForComparison(initialVal)
      ) {
        return true;
      }

      // Compare selected Data Center option
      const currentOpt = fieldOptions[field.id];
      const currentOptId =
        typeof currentOpt === "string" ? currentOpt : currentOpt?.id || "";

      const initialOptId = findInitialDataCenterOptionByName(
        fieldName,
        registryData,
      );

      if (
        normalizeValueForComparison(currentOptId) !==
        normalizeValueForComparison(initialOptId)
      ) {
        return true;
      }
    }

    return false;
  }, [
    registryId,
    regName,
    users,
    registries,
    fieldValues,
    fieldOptions,
    registryData,
    entityData,
    isLoading,
    isLoadingRegistry,
  ]);

  const handleUploadFiles = async (
    fileFields: Array<{ fieldId: string; value: unknown }>,
  ) => {
    // Upload files after saving the registry with the id
    if (fileFields.length > 0) {
      for (const { fieldId, value } of fileFields) {
        if (value instanceof File) {
          await uploadFile({
            entity_id: fieldId,
            file: value,
            entity_type: "expedient_base_registry_field",
            collection: "expedient_base_registry_field",
          });
        } else if (Array.isArray(value)) {
          const newFiles = value.filter((f): f is File => f instanceof File);
          // Upload new files
          await Promise.all(
            newFiles.map((file) =>
              uploadFile({
                entity_id: fieldId,
                file,
                entity_type: "expedient_base_registry_field",
                collection: "expedient_base_registry_field",
              }),
            ),
          );
        }
      }
    }
  };

  const handleDeleteFiles = async (fileIds: string[]) => {
    if (fileIds.length > 0) {
      await Promise.all(fileIds.map((fileId) => deleteFileMutation(fileId)));
    }
  };

  const goBack = useCallback(() => {
    navigate(
      `/expedientes${entityId && registryId ? `/entidad/${entityId}/detalle-registro/${registryId}` : entityId ? `?entity=${entityId}` : ""}`,
    );
  }, [navigate, entityId, registryId]);

  // Handler for the cancel button
  const handleCancelClick = useCallback(() => {
    if (hasUnsavedChanges) {
      onCancel(); // Opens the modal
    } else {
      goBack();
    }
  }, [hasUnsavedChanges, onCancel, goBack]);

  useEffect(() => {
    setComp(
      <>
        <Button
          variant="quiet"
          size="xs"
          type="button"
          onClick={handleCancelClick}
        >
          Cancelar
          <Icon type="fas" name="times" />
        </Button>
        <Button
          variant="default"
          size="xs"
          type="button"
          disabled={isPending || isUpdating || !isFormValid}
          onClick={handleSubmit}
        >
          Guardar cambios
          {isPending || isUpdating ? (
            <Icon
              type="fas"
              name="spinner-third"
              className="animate-spin text-current"
            />
          ) : (
            <Icon type="fas" name="save" className="text-current" />
          )}
        </Button>
      </>,
    );

    return () => {
      setComp(null);
    };
  }, [
    setComp,
    handleSubmit,
    handleCancelClick,
    isPending,
    isUpdating,
    isFormValid,
  ]);

  useEffect(() => {
    if (!registryId && autoIdCode) {
      setRegId(autoIdCode);
    }
  }, [registryId, autoIdCode, setRegId]);

  useEffect(() => {
    if (entityData && entityData.roles) {
      setRolesList(entityData.roles);
    }
  }, [entityData, setRolesList]);

  useEffect(() => {
    if (entityData && entityData.fields) {
      setSections(getSectionsFromEntity(entityData));

      // Initialize default values for new registries
      if (!registryId) {
        const defaultValues = entityData.fields.reduce(
          (acc, field) => {
            if (
              field.default_value !== null &&
              field.default_value !== undefined
            ) {
              // For identification fields, only use the default value as value
              if (field.configuration?.type === "identification") {
                acc[field.id] = {
                  value: field.default_value,
                };
              } else if (field.field_type === "MULTISELECT") {
                acc[field.id] = [
                  {
                    key: field.default_value,
                    label: field.default_value,
                  },
                ];
              } else {
                acc[field.id] = field.default_value;
              }
            }
            return acc;
          },
          {} as Record<string, unknown>,
        );
        setFieldValues(defaultValues);
      }
    }
  }, [entityData, registryData, registryId, setFieldValues]);

  useEffect(() => {
    if (registryData) {
      // Update registry name
      if (registryData.name) {
        setRegName(registryData.name);
      }

      if (registryData.unique_code) {
        setRegId(registryData.unique_code);
      }

      if (registryData.associates && registryData.associates.length > 0) {
        setUsers(registryData.associates);
      }

      // Set related registries
      if (
        registryData.related_registries &&
        registryData.related_registries.length > 0
      ) {
        setRegistries(registryData.related_registries);
      }

      // Extract current field values
      if (registryData.field_values && registryData.field_values.length > 0) {
        const currentFieldValues = registryData.field_values.reduce(
          (acc, fieldValue) => {
            // Search by name and type since id do not match due versioning issues
            const currentFieldId = entityData?.fields.find(
              (f) =>
                f.name === fieldValue.entity_field?.name &&
                f.field_type === fieldValue.entity_field?.field_type,
            )?.id;
            if (!currentFieldId) return acc;

            if (fieldValue.entity_field?.id) {
              let parsedValue: unknown = fieldValue.value;

              // If the field is MULTISELECT and the value is string, parse it to array
              if (
                fieldValue.entity_field.field_type === "MULTISELECT" &&
                typeof parsedValue === "string" &&
                parsedValue
              ) {
                try {
                  parsedValue = JSON.parse(parsedValue);
                } catch {
                  // If parsing fails, convert to single-element array
                  parsedValue = [parsedValue];
                }
              }

              // If the field is URL, assign the files value
              if (fieldValue.entity_field.field_type === "URL") {
                parsedValue =
                  fieldValue.files?.map((file) => ({
                    ...file,
                    name: file.file_name,
                  })) || [];
              }

              // search database values
              let hasDataCenterRelation = false;
              if (fieldValue.entity_field.field_type === "SELECT") {
                const dbRelations = registryData.data_center_relations || [];
                const dbRelation = dbRelations.find(
                  (rel) =>
                    rel.expedient_base_entity_field_id ===
                      fieldValue.expedient_base_entity_field_id &&
                    rel.relation_type === "field_value",
                );
                if (dbRelation) {
                  acc[currentFieldId] = dbRelation.relation_id;
                  hasDataCenterRelation = true;
                }
              }

              // search database values
              if (fieldValue.entity_field.field_type === "MULTISELECT") {
                const dbRelations = registryData.data_center_relations || [];
                const dbRelation = dbRelations.filter(
                  (rel) =>
                    rel.expedient_base_entity_field_id ===
                      fieldValue.expedient_base_entity_field_id &&
                    rel.relation_type === "field_value",
                );
                if (dbRelation && dbRelation.length > 0) {
                  acc[currentFieldId] = dbRelation.map(
                    (rel) => rel.relation_id,
                  );
                  hasDataCenterRelation = true;
                }
              }

              // Only use parsedValue if there's no data center relation
              if (!hasDataCenterRelation) {
                acc[currentFieldId] = parsedValue;
              }
            }
            return acc;
          },
          {} as Record<string, unknown>,
        );
        setFieldValues(currentFieldValues);
      }

      // Extract selected options from dropdowns
      if (registryData.field_values && registryData.field_values.length > 0) {
        const currentFieldOptions = registryData.field_values.reduce(
          (acc, fieldValue) => {
            const currentFieldId = fieldValue.expedient_base_entity_field_id;
            const dbRelations = registryData.data_center_relations || [];
            const dbRelation = dbRelations.find(
              (rel) =>
                rel.expedient_base_entity_field_id ===
                  fieldValue.entity_field?.id &&
                rel.relation_type === "selected_option",
            );
            if (dbRelation) {
              acc[currentFieldId] = {
                display: dbRelation.display_value,
                id: dbRelation.relation_id,
                table: dbRelation.reference_name || "",
                source: "data_center",
              };
            }
            return acc;
          },
          {} as Record<
            string,
            | string
            | { id: string; table: string; display: string; source: string }
          >,
        );

        setFieldOptions(currentFieldOptions);
      }
    }
  }, [
    registryData,
    entityData,
    setFieldValues,
    setRegName,
    setUsers,
    setFieldOptions,
    setRegId,
    setRegistries,
  ]);

  const handleFieldChange = (fieldId: string, value: unknown) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleOptionChange = useCallback(
    (
      fieldId: string,
      option: string,
      label?: string,
      table?: string,
      isDataCenter?: boolean,
    ) => {
      setFieldOptions((prev) => ({
        ...prev,
        [fieldId]: isDataCenter
          ? {
              id: option,
              table: table || "",
              display: label || "",
              source: "data_center",
            }
          : option,
      }));
    },
    [setFieldOptions],
  );

  if (isLoading || isLoadingRegistry) {
    return (
      <div className="flex justify-center mx-auto pt-16">
        <Icon
          name="spinner-third"
          size="lg"
          className="animate-spin text-sm text-on-disabled"
          type="far"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-3 p-8">
      <div className="w-full md:w-1/2">
        <div className="p-4 flex flex-col gap-4">
          <Avatar>
            <AvatarFallback color="neutral">
              <Icon type="fas" name="folder" />
            </AvatarFallback>
          </Avatar>
          <Heading variant="h5" color="loud">
            {regName
              ? regName
              : registryName
                ? "Nombre de " + registryName
                : ""}
          </Heading>
          <div>
            {users.length === 0 ? (
              <p className="text-body-quiet">Sin usuarios asociados</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {users.map((user) => (
                  <Badge key={user.applicant_id} variant="action">
                    <Icon
                      type="fas"
                      name="user"
                      size="xs"
                      className="text-action-quiet"
                    />
                    {`${user.applicant?.first_name || ""} ${user.applicant?.last_name || ""}`}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <hr className="border-neutral-quiet" />
          {sections.map((section) => (
            <div key={section.id} className="flex flex-col">
              <p className="text-body-loud text-size-body leading-3xl font-semibold">
                {section.title}
              </p>

              {section.fields.map((field) => {
                const fieldInfo = getFieldTypeInfo(field);
                return (
                  <FieldCard
                    className="border-none"
                    key={field.id}
                    field={field}
                    fieldInfo={fieldInfo}
                    sectionId={section.id}
                    readonly
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className=" w-full md:w-1/2 bg-neutral-object border border-neutral-quiet rounded-lg p-6">
        <div className="flex flex-col gap-4">
          <TextField
            label={
              registryName ? "Nombre de " + registryName : "Nombre del registro"
            }
            required
            placeholder={
              "Ingrese el nombre " +
              (registryName ? "de " + registryName : "del registro")
            }
            value={regName}
            onChange={(e) => setRegName(e.target.value)}
          />
          <IdField
            label="ID"
            required
            canUnlock
            originalValue={registryData?.unique_code}
            value={regId}
            onChange={(value) => setRegId(value)}
            entityId={entityId}
            currentRegistryId={registryId}
          />

          <UsersList />
          <RegistryList
            registryCode={registryData?.unique_code}
            registries={registryData?.related_registries ?? undefined}
          />
          <hr className="border-neutral-quiet" />
          {sections.map((section, index) => (
            <div key={section.id} className="flex flex-col gap-2">
              <p className="text-body-loud text-size-body-lg font-semibold">
                {section.title}
              </p>
              {section.fields.map((field) => {
                const fieldOption = fieldOptions[field.id];
                const optionVal =
                  typeof fieldOption === "string"
                    ? fieldOption
                    : fieldOption?.id || "";
                return (
                  <RegistryFormField
                    key={field.id}
                    field={field}
                    value={fieldValues[field.id]}
                    optionValue={optionVal}
                    onChange={(value) => handleFieldChange(field.id, value)}
                    onOptionChange={(
                      fieldId,
                      option,
                      label,
                      table,
                      isDataCenter,
                    ) =>
                      handleOptionChange(
                        fieldId,
                        option,
                        label,
                        table,
                        isDataCenter,
                      )
                    }
                  />
                );
              })}
              {index < sections.length - 1 && (
                <hr className="border-neutral-quiet mt-4" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
